import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { loadActiveMembership } from '../lib/organizations';
import {
  requireSupabase,
  supabase,
  supabaseConfigurationError,
} from '../lib/supabase';

const AuthContext = createContext(null);

function normalizeUser(authUser, membership) {
  if (!authUser) return null;
  const metadata = authUser.user_metadata || {};
  const name =
    metadata.full_name ||
    metadata.name ||
    authUser.email?.split('@')[0] ||
    'Clinician';

  return {
    created_at: authUser.created_at,
    email: authUser.email,
    name,
    picture: metadata.avatar_url || metadata.picture || null,
    role: membership?.role || 'clinician',
    user_id: authUser.id,
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [membership, setMembership] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(supabaseConfigurationError);
  const synchronizationVersion = useRef(0);

  const applySession = useCallback(async (nextSession) => {
    const version = ++synchronizationVersion.current;
    setSession(nextSession);

    if (!nextSession?.user) {
      setMembership(null);
      setUser(null);
      setError(supabaseConfigurationError);
      setLoading(false);
      return null;
    }

    setLoading(true);
    try {
      const nextMembership = await loadActiveMembership(nextSession.user.id);
      if (version !== synchronizationVersion.current) return nextMembership;
      setMembership(nextMembership);
      setUser(normalizeUser(nextSession.user, nextMembership));
      setError(null);
      return nextMembership;
    } catch (contextError) {
      if (version !== synchronizationVersion.current) return null;
      setMembership(null);
      setUser(normalizeUser(nextSession.user, null));
      setError(contextError.message || 'Unable to load your workspace');
      return null;
    } finally {
      if (version === synchronizationVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!mounted) return;
      if (sessionError) {
        setError(sessionError.message);
        setLoading(false);
        return;
      }
      applySession(data.session);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      window.setTimeout(() => {
        if (mounted) applySession(nextSession);
      }, 0);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = useCallback(async ({ email, password }) => {
    const client = requireSupabase();
    const { data, error: signInError } = await client.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (signInError) throw signInError;
    await applySession(data.session);
    return data;
  }, [applySession]);

  // Sign into the shared, read-only demo workspace using build-time credentials.
  const signInDemo = useCallback(async () => {
    const email = process.env.REACT_APP_DEMO_EMAIL;
    const password = process.env.REACT_APP_DEMO_PASSWORD;
    if (!email || !password) {
      throw new Error('The live demo is not configured.');
    }
    return signIn({ email, password });
  }, [signIn]);

  const signUp = useCallback(async ({ email, fullName, password }) => {
    const client = requireSupabase();
    const { data, error: signUpError } = await client.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: { full_name: fullName.trim() },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (signUpError) throw signUpError;
    if (data.session) await applySession(data.session);
    return data;
  }, [applySession]);

  // Send a password-recovery email. The link returns to /auth/callback, which
  // routes the user to the set-password screen (same as invites).
  const resetPassword = useCallback(async (email) => {
    const client = requireSupabase();
    const { error: resetError } = await client.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
    if (resetError) throw resetError;
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const client = requireSupabase();
    const { error: oauthError } = await client.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (oauthError) throw oauthError;
  }, []);

  const completeOAuth = useCallback(async (code) => {
    const client = requireSupabase();
    const { data, error: exchangeError } = await client.auth.exchangeCodeForSession(code);
    if (exchangeError) throw exchangeError;
    await applySession(data.session);
    return data;
  }, [applySession]);

  // Verify an emailed one-time token (invite, recovery, signup, magiclink) via
  // its token_hash. Works regardless of PKCE since no browser code_verifier is
  // required — this is how admin-generated invite links are consumed.
  const verifyOtp = useCallback(async ({ tokenHash, type }) => {
    const client = requireSupabase();
    const { data, error: otpError } = await client.auth.verifyOtp({
      token_hash: tokenHash,
      type,
    });
    if (otpError) throw otpError;
    await applySession(data.session);
    return data;
  }, [applySession]);

  // Establish a session from tokens delivered in the URL hash (implicit-style
  // redirect from the GoTrue verify endpoint).
  const establishSession = useCallback(async ({ accessToken, refreshToken }) => {
    const client = requireSupabase();
    const { data, error: sessionError } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (sessionError) throw sessionError;
    await applySession(data.session);
    return data;
  }, [applySession]);

  const logout = useCallback(async () => {
    const client = requireSupabase();
    const { error: signOutError } = await client.auth.signOut();
    if (signOutError) throw signOutError;
    await applySession(null);
  }, [applySession]);

  const refreshMembership = useCallback(async () => {
    if (!session?.user) return null;
    return applySession(session);
  }, [applySession, session]);

  const refreshUser = useCallback(async () => {
    const client = requireSupabase();
    // Pull the authoritative user (fresh user_metadata) from the server so a
    // name change made elsewhere (e.g. the admin panel) reflects immediately.
    const { data, error: userError } = await client.auth.getUser();
    if (userError || !data?.user) return null;
    const base = session || (await client.auth.getSession()).data.session;
    if (!base) return null;
    return applySession({ ...base, user: data.user });
  }, [applySession, session]);

  // Set/replace the current user's password (used by invited users and by the
  // password-recovery flow). Requires an active session.
  const updatePassword = useCallback(async (password) => {
    const client = requireSupabase();
    const { data, error: updateError } = await client.auth.updateUser({ password });
    if (updateError) throw updateError;
    await refreshUser();
    return data;
  }, [refreshUser]);

  const value = useMemo(() => ({
    authError: error,
    completeOAuth,
    establishSession,
    isDemo: membership?.organization?.slug === 'demo',
    loading,
    logout,
    membership,
    organization: membership?.organization || null,
    refreshMembership,
    refreshUser,
    resetPassword,
    session,
    signIn,
    signInDemo,
    signInWithGoogle,
    signUp,
    updatePassword,
    user,
    verifyOtp,
  }), [
    completeOAuth,
    error,
    establishSession,
    loading,
    logout,
    membership,
    refreshMembership,
    refreshUser,
    resetPassword,
    session,
    signIn,
    signInDemo,
    signInWithGoogle,
    signUp,
    updatePassword,
    user,
    verifyOtp,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
